"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumHierarchicalSchematicProcessor = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AltiumSchematicTransformer_1 = require("./AltiumSchematicTransformer");
const SchematicInstanceTracker_1 = require("./SchematicInstanceTracker");
const AltiumNetNamingStrategy_1 = require("../nets/AltiumNetNamingStrategy");
const altiumProjectFileTypes_1 = require("../../project/altiumProjectFileTypes");
const altiumValidation_1 = require("../../../altiumValidation");
const altiumFileTypes_1 = require("../../../altiumFileTypes");
const AltiumSheetSymbol_1 = require("../helpers/AltiumSheetSymbol");
const layers_1 = require("../../../layers");
const AltiumNameGenerator_1 = require("./AltiumNameGenerator");
const AltiumFontMapper_1 = require("../helpers/AltiumFontMapper");
const AltiumAnnotationFileUtil_1 = require("../../annotations/AltiumAnnotationFileUtil");
const PathUtils_1 = require("../../common/PathUtils");
const NetCombobulator_1 = require("../../../../NetCombobulation/NetCombobulator");
const remediation_1 = require("../../../../../Types/remediation");
const AltiumCombobulatorConfig_1 = require("./AltiumCombobulatorConfig");
const AltiumChannelNameOrder_1 = require("./AltiumChannelNameOrder");
class AltiumHierarchicalSchematicProcessor {
    constructor(parsedDef, projectConfigUtil, options = {}) {
        this.parsedDef = parsedDef;
        this.projectConfigUtil = projectConfigUtil;
        this.instances = new Map();
        this.instanceMap = new Map();
        this.schematicTransformers = new Map();
        this.processingResults = [];
        this.flatNamingContext = undefined;
        this.usedSymbols = new WeakSet();
        this.hierarchyInstanceTrackers = new Map();
        this.physicalChannelIndexes = new Map();
        this.rootHierarchies = this.resolveHierarchies();
        this.hierarchyMode = this.resolveHierarchyMode();
        if (this.projectConfigUtil.checkHierarchyModeRequiresProjectStructure(this.hierarchyMode) &&
            !this.projectConfigUtil.hasPrjpcbStructureFile) {
            throw new Error(`Hierarchy mode ${this.hierarchyMode} requires the project structure file to be present`);
        }
        const isGlobalMode = this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Global;
        if (isGlobalMode) {
            this.flatNamingContext = new sigma_engine_shared_1.NetRefCtx();
        }
        this.fontMapper = new AltiumFontMapper_1.AltiumFontMapper(parsedDef);
        this.projectModel = {
            type: 'altium',
            layerSets: {},
            designs: {},
            components: {},
            modules: [],
            warnings: [],
            processingResults: {
                type: 'project-processing',
                message: 'Project processed successfully',
                innerResults: [],
            },
            fonts: {
                fonts: Object.fromEntries(this.fontMapper.projectFonts),
            },
            globalNets: {},
        };
        this.annotationUtil = new AltiumAnnotationFileUtil_1.AltiumAnnotationFileUtil(this.parsedDef, this.projectConfigUtil);
        for (const diagnostic of this.annotationUtil.annotationDiagnostics) {
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: diagnostic,
            });
        }
        if (this.usesCompiledSheetNumbering()) {
            for (const diagnostic of this.annotationUtil.sheetNumberDiagnostics) {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: diagnostic,
                });
            }
        }
        this.nameGenerator = new AltiumNameGenerator_1.AltiumNameGenerator(this.projectModel, this.parsedDef, this.projectConfigUtil, this.annotationUtil, (result) => this.projectModel.processingResults.innerResults.push(result));
        this.namingStrategy = new AltiumNetNamingStrategy_1.AltiumNetNamingStrategy(this.projectConfigUtil, this.nameGenerator, this.hierarchyMode, {
            enableDebug: options.netNamingDebug,
        });
    }
    getNetNamingDebug() {
        return this.namingStrategy.getNetNamingDebug();
    }
    get hierarchyInstances() {
        return Array.from(this.instanceMap.values());
    }
    getHierarchyInstanceTracker(sourceFile) {
        let tracker = this.hierarchyInstanceTrackers.get(sourceFile);
        if (!tracker) {
            tracker = new SchematicInstanceTracker_1.SchematicInstanceTracker(sourceFile, this.projectConfigUtil);
            this.hierarchyInstanceTrackers.set(sourceFile, tracker);
        }
        return tracker;
    }
    initializeInstances(schematicHierarchy, parentInstance, fileDesignatorCounts, _projectConfigUtil, physicalChannelIndexes) {
        const REPEAT_REGEX = /Repeat\(\s*([^,]+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i;
        if (!parentInstance) {
            const roomName = sigma_engine_shared_1.Files.basenamePath(schematicHierarchy.name.replace(/\.SchDoc$/i, ''));
            return [
                {
                    instanceId: schematicHierarchy.name,
                    hierarchyEntry: schematicHierarchy,
                    path: [],
                    pathUniqueId: '',
                    sheetSymbol: undefined,
                    numInstances: 1,
                    totalSheetInstances: 1,
                    instanceIndex: 0,
                    channelIndex: 0,
                    isMultiChannel: false,
                    roomName: roomName,
                    channelPrefix: roomName,
                    physicalRoomName: roomName,
                    physicalChannelPrefix: roomName,
                    physicalChannelIndex: 0,
                    children: [],
                },
            ];
        }
        const sortedAllSheetSymbols = parentInstance.hierarchyEntry.schematic
            .getSheetSymbols()
            .map((sheetSymbol, sourceOrder) => ({ sheetSymbol, sourceOrder }))
            .sort((a, b) => {
            const nameOrder = (0, AltiumChannelNameOrder_1.compareAltiumChannelNames)(a.sheetSymbol.getSheetName().text, b.sheetSymbol.getSheetName().text);
            return nameOrder || b.sourceOrder - a.sourceOrder;
        });
        const sortedSheetSymbols = sortedAllSheetSymbols
            .filter(({ sheetSymbol }) => {
            return this.resolveSchematicKey(sheetSymbol.getFileNameText()) === schematicHierarchy.sourceFile;
        })
            .map(({ sheetSymbol }) => sheetSymbol);
        return this.updateDesignatorCounts(fileDesignatorCounts, sortedSheetSymbols.flatMap((sheetSymbol) => {
            const sheetName = sheetSymbol.getSheetNameText();
            const repeatMatch = sheetName.match(REPEAT_REGEX);
            if (repeatMatch) {
                const channelPrefix = repeatMatch[1];
                const channelIndex1 = parseInt(repeatMatch[2]);
                const lastChannelIndexN = parseInt(repeatMatch[3]);
                if (typeof channelIndex1 !== 'number' ||
                    typeof lastChannelIndexN !== 'number' ||
                    Number.isNaN(channelIndex1) ||
                    Number.isNaN(lastChannelIndexN)) {
                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} or ${repeatMatch[3]} is not a number`);
                }
                if (channelIndex1 < 0 || lastChannelIndexN < 0) {
                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} or ${repeatMatch[3]} is negative`);
                }
                if (channelIndex1 > lastChannelIndexN) {
                    throw new Error(`Invalid channel index in sheet name ${sheetName} - ${repeatMatch[2]} is greater than ${repeatMatch[3]}`);
                }
                const instances = [];
                for (let i = channelIndex1; i <= lastChannelIndexN; i++) {
                    const physicalChannelIndex = (physicalChannelIndexes.get(schematicHierarchy.sourceFile) ?? 0) + 1;
                    physicalChannelIndexes.set(schematicHierarchy.sourceFile, physicalChannelIndex);
                    const uniqueIdNo = schematicHierarchy.instanceTracker.claimNextSheetIndexInPath(parentInstance, schematicHierarchy, i);
                    const uniqueId = `${uniqueIdNo}${sheetSymbol.uniqueId}`;
                    const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(parentInstance.pathUniqueId, uniqueId);
                    instances.push({
                        instanceId: `${schematicHierarchy.name}:${pathUniqueId}`,
                        hierarchyEntry: schematicHierarchy,
                        path: [...parentInstance.path, parentInstance],
                        pathUniqueId,
                        sheetSymbol,
                        numInstances: lastChannelIndexN - channelIndex1 + 1,
                        totalSheetInstances: 0,
                        instanceIndex: i - channelIndex1,
                        isMultiChannel: true,
                        channelPrefix,
                        channelIndex: i,
                        roomChannelIndex: i,
                        roomAlphaIndex: i,
                        roomName: channelPrefix,
                        physicalRoomName: channelPrefix,
                        physicalChannelPrefix: channelPrefix,
                        physicalChannelIndex,
                        children: [],
                    });
                }
                return instances;
            }
            const channelPrefix = sheetSymbol.getSheetNameText();
            const pathUniqueId = (0, PathUtils_1.joinPathUniqueIds)(parentInstance.pathUniqueId, sheetSymbol.uniqueId);
            const plainPhysicalChannelIndex = (physicalChannelIndexes.get(schematicHierarchy.sourceFile) ?? 0) + 1;
            physicalChannelIndexes.set(schematicHierarchy.sourceFile, plainPhysicalChannelIndex);
            return [
                {
                    instanceId: `${schematicHierarchy.name}:${pathUniqueId}`,
                    hierarchyEntry: schematicHierarchy,
                    path: [...parentInstance.path, parentInstance],
                    pathUniqueId,
                    sheetSymbol,
                    numInstances: 1,
                    totalSheetInstances: 0,
                    instanceIndex: 0,
                    channelIndex: 1,
                    roomAlphaIndex: undefined,
                    isMultiChannel: false,
                    channelPrefix,
                    roomName: channelPrefix,
                    physicalRoomName: channelPrefix,
                    physicalChannelPrefix: channelPrefix,
                    physicalChannelIndex: plainPhysicalChannelIndex,
                    children: [],
                },
            ];
        }));
    }
    updateDesignatorCounts(fileDesignatorCounts, instances) {
        const totalNumInstances = instances.reduce((p, c) => p + c.numInstances, 0);
        for (const instance of instances) {
            let designatorCount = fileDesignatorCounts.get(instance.channelPrefix);
            if (!designatorCount) {
                designatorCount = 0;
            }
            fileDesignatorCounts.set(instance.channelPrefix, ++designatorCount);
            instance.totalSheetInstances = totalNumInstances;
            const lastParent = instance.path[instance.path.length - 1];
            if (lastParent) {
                lastParent.children.push(instance);
            }
        }
        return instances;
    }
    schematicFileKey(fileName) {
        const base = sigma_engine_shared_1.Files.basenamePath(fileName.replace(/\\/g, '/'));
        return (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(base).toLowerCase();
    }
    resolveSchematicKey(fileName) {
        const normalizedFileName = fileName.replace(/\\/g, '/');
        const exactKeys = Object.keys(this.parsedDef.schematics).filter((key) => key.replace(/\\/g, '/') === normalizedFileName);
        if (exactKeys.length === 1) {
            return exactKeys[0];
        }
        const resolution = this.projectConfigUtil.resolveProjectDocumentPath(fileName);
        if (resolution.documentPath) {
            const projectDocumentPath = resolution.documentPath;
            const projectMatches = Object.keys(this.parsedDef.schematics).filter((key) => this.projectConfigUtil.resolveProjectDocumentPath(key).documentPath === projectDocumentPath);
            if (projectMatches.length === 1) {
                return projectMatches[0];
            }
            if (projectMatches.length > 1) {
                throw new Error(`Ambiguous schematic reference "${fileName}": matches ${projectMatches.join(', ')}`);
            }
        }
        const target = this.schematicFileKey(fileName);
        const matches = Object.keys(this.parsedDef.schematics).filter((key) => this.schematicFileKey(key) === target);
        if (matches.length > 1) {
            throw new Error(`Ambiguous schematic reference "${fileName}": matches ${matches.join(', ')}`);
        }
        return matches[0];
    }
    resolveProjectDocumentPath(fileKey, reportZeroMatch = false) {
        const reportedKeys = this.reportedProjectDocumentPathKeys ?? new Set();
        this.reportedProjectDocumentPathKeys = reportedKeys;
        const resolution = this.projectConfigUtil.resolveProjectDocumentPath(fileKey);
        if (reportZeroMatch && resolution.documentPath === undefined) {
            if (!reportedKeys.has(fileKey)) {
                reportedKeys.add(fileKey);
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                    message: `No project document path matches schematic key ${fileKey}`,
                });
            }
        }
        return resolution.documentPath;
    }
    resolveHierarchies() {
        const schematicHighestHierarchyIndexes = new Map();
        const builtHierarchies = new Map();
        const roots = this.resolveRootDocumentFileNames().flatMap((rawFileName) => {
            const skipRoot = (message) => {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message,
                });
                return [];
            };
            const fileName = this.resolveSchematicKey(rawFileName) ?? rawFileName;
            const schematicDef = this.parsedDef.schematics[fileName];
            if (!schematicDef) {
                return skipRoot(`Root schematic ${rawFileName} not found — skipping the missing document`);
            }
            if (!schematicDef.validationResult?.isValid) {
                return skipRoot(`Root schematic ${fileName} is not valid — skipping the document`);
            }
            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
            if (!schematic) {
                return skipRoot(`Parsed root schematic ${fileName} is not available — skipping the document`);
            }
            let schematicIndex = 0;
            let sheetNumber;
            try {
                sheetNumber = this.projectConfigUtil.getAltiumSchematicSheetNumber(fileName);
                if (!Number.isInteger(sheetNumber) || sheetNumber < 1) {
                    throw new Error(`Invalid sheet number for ${fileName}`);
                }
                schematicIndex = sheetNumber - 1;
            }
            catch (e) {
                sheetNumber = undefined;
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Failed to get Altium schematic index for ${fileName}: ${e}`,
                });
            }
            const highestHierarchyIndex = schematicHighestHierarchyIndexes.get(fileName) ?? 0;
            schematicHighestHierarchyIndexes.set(fileName, highestHierarchyIndex);
            return this.buildChildHierarchies({
                name: fileName.replace(/\.SchDoc$/i, ''),
                sourceFile: fileName,
                projectDocumentPath: this.resolveProjectDocumentPath(fileName, true),
                schematicIndex,
                sheetNumber,
                children: [],
                parents: [],
                schematic,
                instanceTracker: this.getHierarchyInstanceTracker(fileName),
                hierarchyIndex: highestHierarchyIndex,
            }, schematicHighestHierarchyIndexes, builtHierarchies);
        });
        if (roots.length === 0) {
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: 'No root hierarchies found',
            });
        }
        else if (roots.length > 1 &&
            (this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical)) {
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: 'Invalid Altium project: Multiple root hierarchies found',
            });
        }
        return roots;
    }
    resolveRootDocumentFileNames() {
        const configuredHierarchyMode = this.projectConfigUtil.getDesignConfiguration('HierarchyMode') ?? altiumProjectFileTypes_1.HierarchyMode.Automatic;
        if (configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical ||
            configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
            if (this.projectConfigUtil.listsNoSchematics) {
                throw new Error(`${this.noSheetsFailureCode()}: Hierarchy mode ${configuredHierarchyMode} needs the project to name its top sheet, but the project file lists no schematic sheets and there is no project structure file`);
            }
            return this.projectConfigUtil.getTopLevelDocuments('.SchDoc');
        }
        const topLevelDocuments = this.resolveTopLevelDocuments();
        if (this.projectConfigUtil.listsNoSchematics) {
            return topLevelDocuments;
        }
        if (configuredHierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Automatic &&
            this.topLevelDocumentsContainSheetEntries(topLevelDocuments)) {
            return topLevelDocuments;
        }
        return this.projectConfigUtil.getFlatProjectDocuments('.SchDoc');
    }
    resolveTopLevelDocuments() {
        if (!this.projectConfigUtil.listsNoSchematics) {
            return this.projectConfigUtil.getTopLevelDocuments('.SchDoc');
        }
        if (this.fallbackRootDocuments === undefined) {
            const roots = this.resolveUploadedRootDocuments();
            const projectFileName = this.projectConfigUtil.projectFileName;
            const listing = projectFileName ?
                `Project file ${projectFileName} lists no schematic sheets and there is no project structure file`
                : 'No Altium project file was uploaded';
            if (roots.length === 0) {
                throw new Error(`${this.noSheetsFailureCode()}: ${listing}, and no schematic was uploaded to use instead`);
            }
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                message: `${listing}; using the uploaded schematics as flat top-level sheets: ${roots.join(', ')}`,
            });
            this.fallbackRootDocuments = roots;
        }
        return this.fallbackRootDocuments;
    }
    resolveUploadedRootDocuments() {
        const uploadedKeys = Object.keys(this.parsedDef.schematics)
            .filter((key) => key.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.SchDoc.toLowerCase()))
            .sort();
        const referencedKeys = new Set();
        for (const key of uploadedKeys) {
            const schematicDef = this.parsedDef.schematics[key];
            if (!schematicDef?.validationResult?.isValid) {
                continue;
            }
            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
            for (const sheetSymbol of schematic?.getSheetSymbols() ?? []) {
                const reference = (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(sheetSymbol.getFileNameText());
                const referencedKey = reference ? this.resolveSchematicKey(reference) : undefined;
                if (referencedKey !== undefined && referencedKey !== key) {
                    referencedKeys.add(referencedKey);
                }
            }
        }
        return uploadedKeys.filter((key) => !referencedKeys.has(key));
    }
    noSheetsFailureCode() {
        return this.projectConfigUtil.projectFileName ?
            remediation_1.ProjectParseFailureCode.AltiumProjectListsNoSheets
            : remediation_1.ProjectParseFailureCode.AltiumProjectFileMissing;
    }
    topLevelDocumentsContainSheetEntries(fileNames) {
        const pending = [...fileNames];
        const visited = new Set();
        for (const fileName of pending) {
            const key = this.resolveSchematicKey(fileName) ?? fileName;
            if (visited.has(key)) {
                continue;
            }
            visited.add(key);
            const schematicDef = this.parsedDef.schematics[key];
            if (!schematicDef?.validationResult?.isValid) {
                continue;
            }
            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
            for (const sheetSymbol of schematic?.getSheetSymbols() ?? []) {
                if (sheetSymbol.getEntries().length > 0) {
                    return true;
                }
                pending.push(sheetSymbol.getFileNameText());
            }
        }
        return false;
    }
    buildChildHierarchies(parentHierarchy, schematicHighestHierarchyIndexes, builtHierarchies) {
        builtHierarchies.set(parentHierarchy.sourceFile, parentHierarchy);
        for (const sheet of parentHierarchy.schematic.getSheetSymbols()) {
            const referencedFileName = sheet.getFileNameText();
            const fileName = this.resolveSchematicKey(referencedFileName) ?? referencedFileName;
            const currentHierarchyIndex = parentHierarchy.hierarchyIndex + 1;
            let highestHierarchyIndex = schematicHighestHierarchyIndexes.get(fileName) ?? currentHierarchyIndex;
            if (highestHierarchyIndex > currentHierarchyIndex) {
                highestHierarchyIndex = currentHierarchyIndex;
            }
            schematicHighestHierarchyIndexes.set(fileName, highestHierarchyIndex);
            const builtHierarchy = builtHierarchies.get(fileName);
            if (builtHierarchy) {
                if (builtHierarchy.hierarchyIndex > currentHierarchyIndex) {
                    builtHierarchy.hierarchyIndex = currentHierarchyIndex;
                }
                if (!parentHierarchy.children.some((c) => c.sourceFile === fileName)) {
                    parentHierarchy.children.push(builtHierarchies.get(fileName));
                }
                continue;
            }
            const schematicDef = this.parsedDef.schematics[fileName];
            if (!schematicDef) {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Child schematic ${referencedFileName} not found — skipping the missing document`,
                });
                continue;
            }
            if (!schematicDef.validationResult?.isValid) {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Child schematic ${fileName} is not valid — skipping the document`,
                });
                continue;
            }
            const schematic = schematicDef.validationResult.data.metadata[altiumValidation_1.AltiumProjectMetadataKey.SchDoc];
            if (!schematic) {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Parsed child schematic ${fileName} is not available — skipping the document`,
                });
                continue;
            }
            let schematicIndex = 0;
            let sheetNumber;
            try {
                sheetNumber = this.projectConfigUtil.getAltiumSchematicSheetNumber(fileName);
                if (!Number.isInteger(sheetNumber) || sheetNumber < 1) {
                    throw new Error(`Invalid sheet number for ${fileName}`);
                }
                schematicIndex = sheetNumber - 1;
            }
            catch (e) {
                sheetNumber = undefined;
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    message: `Failed to get Altium schematic index for ${fileName}: ${e}`,
                });
            }
            const childHierarchy = this.buildChildHierarchies({
                name: fileName.replace(/\.SchDoc$/i, ''),
                sourceFile: fileName,
                projectDocumentPath: this.resolveProjectDocumentPath(fileName, true),
                schematicIndex,
                sheetNumber,
                children: [],
                parents: [parentHierarchy],
                schematic,
                instanceTracker: this.getHierarchyInstanceTracker(fileName),
                hierarchyIndex: highestHierarchyIndex,
            }, schematicHighestHierarchyIndexes, builtHierarchies);
            parentHierarchy.children.push(childHierarchy);
        }
        return parentHierarchy;
    }
    resolveHierarchyMode() {
        let hierarchyMode = this.projectConfigUtil.getDesignConfiguration('HierarchyMode');
        if (hierarchyMode === undefined) {
            hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Automatic;
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                message: 'HierarchyMode is not set in the project config, using Automatic',
            });
        }
        if (hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Automatic) {
            const rootHierarchy = this.rootHierarchies[0];
            if (!rootHierarchy) {
                return altiumProjectFileTypes_1.HierarchyMode.Flat;
            }
            if (this.projectConfigUtil.listsNoSchematics) {
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                    message: 'HierarchyMode is Automatic, using Flat because the project names no schematic sheet to start from',
                });
                return altiumProjectFileTypes_1.HierarchyMode.Flat;
            }
            const topLevelDocuments = this.resolveTopLevelDocuments();
            if (this.topLevelDocumentsContainSheetEntries(topLevelDocuments)) {
                hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Hierarchical;
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                    message: 'HierarchyMode is Automatic, using Hierarchical because there are sheet entries on the top sheet',
                });
                return hierarchyMode;
            }
            const ports = rootHierarchy.schematic.getPorts();
            if (ports.length > 0) {
                hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Flat;
                this.processingResults.push({
                    type: 'hierarchical-schematic-processing',
                    severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                    message: 'HierarchyMode is Automatic, using Flat because there are ports on the top sheet',
                });
                return hierarchyMode;
            }
            hierarchyMode = altiumProjectFileTypes_1.HierarchyMode.Global;
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Recommendation,
                message: 'HierarchyMode is Automatic, using Global because there are no sheet entries or ports on the top sheet',
            });
        }
        return hierarchyMode;
    }
    buildInstanceMap() {
        this.physicalChannelIndexes.clear();
        const traversals = this.rootHierarchies.map((hierarchy) => this.traverseHierarchy(hierarchy, []));
        const sourceInstanceCounts = new Map();
        for (const traversal of traversals) {
            for (const [sourceFile, instances] of traversal.schematicInstances) {
                sourceInstanceCounts.set(sourceFile, (sourceInstanceCounts.get(sourceFile) ?? 0) + instances.length);
            }
        }
        const designWideTotals = this.computeDesignWideInstanceTotals(AltiumHierarchicalSchematicProcessor.mergeFileDesignatorCounts(traversals.map((traversal) => traversal.fileDesignatorCounts)));
        for (const traversal of traversals) {
            for (const [sourceFile, instances] of traversal.schematicInstances) {
                for (const instance of instances) {
                    this.resolveCompiledSheetNumber(instance, sourceInstanceCounts.get(sourceFile) ?? 0);
                }
            }
            this.setChannelIndexes(traversal.rootInstance, traversal.parentHierarchyMap, traversal.fileDesignatorCounts, designWideTotals);
            this.createTransformers(traversal.schematicInstances);
        }
        for (const transformer of this.schematicTransformers.values()) {
            transformer.prepareModuleAnnotationClaims();
        }
        for (const transformer of this.schematicTransformers.values()) {
            transformer.initializeModuleNames();
        }
        for (const transformer of this.schematicTransformers.values()) {
            transformer.initializeSchematicModel();
        }
    }
    static mergeFileDesignatorCounts(perRootCounts) {
        const merged = new Map();
        for (const rootCounts of perRootCounts) {
            for (const [sourceFile, designatorCounts] of rootCounts) {
                const mergedDesignatorCounts = merged.get(sourceFile) ?? new Map();
                merged.set(sourceFile, mergedDesignatorCounts);
                for (const [channelPrefix, count] of designatorCounts) {
                    mergedDesignatorCounts.set(channelPrefix, (mergedDesignatorCounts.get(channelPrefix) ?? 0) + count);
                }
            }
        }
        return merged;
    }
    traverseHierarchy(hierarchy, path) {
        const schematicInstances = new Map();
        const fileDesignatorCounts = new Map();
        const queue = [];
        queue.push({
            hierarchy,
            path,
            parentInstance: path[path.length - 1],
        });
        let rootInstance = undefined;
        const parentHierarchyMap = new Map();
        while (queue.length > 0) {
            const current = queue.shift();
            let designatorCounts = fileDesignatorCounts.get(current.hierarchy.sourceFile);
            if (!designatorCounts) {
                designatorCounts = new Map();
                fileDesignatorCounts.set(current.hierarchy.sourceFile, designatorCounts);
            }
            const instances = this.initializeInstances(current.hierarchy, current.parentInstance, designatorCounts, this.projectConfigUtil, this.physicalChannelIndexes);
            const sourceFile = current.hierarchy.sourceFile;
            if (!schematicInstances.has(sourceFile)) {
                schematicInstances.set(sourceFile, []);
            }
            schematicInstances.get(sourceFile).push(...instances);
            for (const instance of instances) {
                if (!rootInstance) {
                    if (instances.length > 1) {
                        throw new Error('Multiple root instances found');
                    }
                    rootInstance = instance;
                }
                else {
                    if (!current.parentInstance) {
                        throw new Error('No parent instance found');
                    }
                    if (!parentHierarchyMap.has(current.parentInstance)) {
                        parentHierarchyMap.set(current.parentInstance, []);
                    }
                    parentHierarchyMap.get(current.parentInstance).push(instance);
                }
                this.instanceMap.set(instance.instanceId, instance);
                for (let i = 0; i < current.hierarchy.children.length; i++) {
                    const child = current.hierarchy.children[i];
                    queue.push({
                        hierarchy: child,
                        path: [...current.path, instance],
                        parentInstance: instance,
                    });
                }
            }
        }
        if (!rootInstance) {
            throw new Error('No root instance found');
        }
        return { rootInstance, parentHierarchyMap, schematicInstances, fileDesignatorCounts };
    }
    resolveCompiledSheetNumber(instance, sourceInstanceCount) {
        if (!this.usesCompiledSheetNumbering()) {
            return;
        }
        const resolution = this.annotationUtil.getCompiledSheetNumber(instance.pathUniqueId, instance.hierarchyEntry.projectDocumentPath ?? instance.hierarchyEntry.sourceFile);
        if (resolution.sheetNumber !== undefined) {
            instance.compiledSheetNumber = resolution.sheetNumber;
            return;
        }
        if (resolution.hasCompiledSheetNumberData) {
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: resolution.diagnostic ??
                    `Could not resolve a unique compiled sheet-number record for ${instance.hierarchyEntry.sourceFile}`,
            });
            return;
        }
        const sourceFile = instance.hierarchyEntry.sourceFile;
        try {
            if (sourceInstanceCount !== 1) {
                throw new Error(`Structural sheet number for ${sourceFile} requires one hierarchy placement; found ${sourceInstanceCount}`);
            }
            const structuralSheetNumber = this.projectConfigUtil.getUnambiguousAltiumSchematicSheetNumber(sourceFile);
            if (!Number.isInteger(structuralSheetNumber) || structuralSheetNumber < 1) {
                throw new Error(`Invalid sheet number for ${sourceFile}`);
            }
            instance.compiledSheetNumber = structuralSheetNumber;
        }
        catch (error) {
            const structuralDiagnostic = error instanceof Error ? error.message : String(error);
            this.processingResults.push({
                type: 'hierarchical-schematic-processing',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
                message: structuralDiagnostic,
            });
        }
    }
    usesCompiledSheetNumbering() {
        return (this.projectConfigUtil.getDesignConfigurationBoolean('AppendSheetNumberToLocalNets') &&
            this.projectConfigUtil.getDesignConfigurationBoolean('AutoSheetNumbering') &&
            this.projectConfigUtil.getDesignConfigurationBoolean('ReorderDocumentsOnCompile'));
    }
    createTransformers(schematicInstances) {
        const rootSourceFiles = new Set(this.rootHierarchies.map((hierarchy) => hierarchy.sourceFile));
        for (const [sourceFile, instances] of schematicInstances) {
            const isOwnRootTraversal = instances.some((instance) => instance.path.length === 0);
            if (this.schematicTransformers.has(sourceFile) && rootSourceFiles.has(sourceFile) && !isOwnRootTraversal) {
                continue;
            }
            const hierarchyEntry = instances[0].hierarchyEntry;
            const instanceCombobulators = new Map();
            for (const instance of instances) {
                instanceCombobulators.set(instance, this.createInstanceCombobulator(instance.instanceId));
            }
            const existingTransformer = rootSourceFiles.has(sourceFile) ? undefined : this.schematicTransformers.get(sourceFile);
            if (existingTransformer) {
                existingTransformer.addInstances(instances, instanceCombobulators);
                continue;
            }
            const transformer = new AltiumSchematicTransformer_1.AltiumSchematicTransformer(hierarchyEntry.schematic, this.parsedDef, this.projectConfigUtil, this.nameGenerator, instances, this.projectModel, instanceCombobulators, this.fontMapper, this.namingStrategy, this.usedSymbols);
            this.schematicTransformers.set(sourceFile, transformer);
        }
    }
    resolveChannelRoomName(instance) {
        const style = this.projectConfigUtil.getDesignConfiguration('ChannelRoomNamingStyle') ??
            altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames;
        const parentIsMultiChannel = instance.path.some((ancestor) => ancestor.isMultiChannel || ancestor.numInstances > 1);
        const localChannelIndex = instance.roomChannelIndex ?? instance.channelIndex;
        const localAlphaIndex = instance.roomAlphaIndex ?? instance.instanceIndex + 1;
        const alphaIndex = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatAlphaWithNames && parentIsMultiChannel ?
            (instance.physicalChannelIndex ?? localAlphaIndex + 1) - 1
            : localAlphaIndex;
        const usesAlpha = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatAlphaWithNames ||
            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.AlphaNamePath ||
            (style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.MixedNamePath && !parentIsMultiChannel);
        const localRoomName = usesAlpha ?
            `${instance.channelPrefix}${(0, AltiumNameGenerator_1.channelAlphaSuffix)(alphaIndex + 1)}`
            : `${instance.channelPrefix}${style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames ? instance.channelIndex : localChannelIndex}`;
        const usesPath = style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.NumericNamePath ||
            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.AlphaNamePath ||
            style === altiumProjectFileTypes_1.ChannelRoomNamingStyle.MixedNamePath;
        const parent = instance.path.at(-1);
        if (usesPath && parent && (parent.isMultiChannel || parent.numInstances > 1)) {
            const separator = this.projectConfigUtil.getDesignConfiguration('ChannelRoomLevelSeperator');
            return `${parent.roomName}${separator}${localRoomName}`;
        }
        return localRoomName;
    }
    setChannelIndexes(parentInstance, schematicInstances, fileDesignatorCounts, designWideTotals = this.computeDesignWideInstanceTotals(fileDesignatorCounts)) {
        if (parentInstance.numInstances > 1) {
            if (!parentInstance.channelPrefix) {
                throw new Error(`Invariant failed: Expected channel prefix for instance ${parentInstance.pathUniqueId}`);
            }
            const channelName = this.annotationUtil.getChannelNameByUniqueId(parentInstance.pathUniqueId);
            if (!parentInstance.isMultiChannel && channelName) {
                if (channelName) {
                    if (!channelName.startsWith(parentInstance.channelPrefix)) {
                        throw new Error(`Channel name ${channelName} does not start with channel prefix ${parentInstance.channelPrefix}`);
                    }
                    const channelIndexStr = channelName.substring(parentInstance.channelPrefix.length);
                    if (!/^\d+$/.test(channelIndexStr)) {
                        throw new Error(`Channel index ${channelIndexStr} is not a number`);
                    }
                    parentInstance.channelIndex = parseInt(channelIndexStr, 10);
                    parentInstance.roomName = channelName;
                    if (!parentInstance.inheritsPhysicalNamingContext) {
                        parentInstance.physicalChannelIndex = this.getPhysicalSiblingChannelIndex(parentInstance);
                        this.setPhysicalNamingContext(parentInstance, parentInstance);
                    }
                    parentInstance.hierarchyEntry.instanceTracker.setClaimedChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex);
                }
            }
            else {
                const channelIndex = parentInstance.isMultiChannel ?
                    parentInstance.hierarchyEntry.instanceTracker.claimNextChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex)
                    : parentInstance.hierarchyEntry.instanceTracker.claimPreferredOrLowestAvailableChannelIndex(parentInstance.channelPrefix, parentInstance.channelIndex);
                parentInstance.channelIndex = channelIndex;
                parentInstance.roomName = this.resolveChannelRoomName(parentInstance);
                if (!parentInstance.inheritsPhysicalNamingContext) {
                    this.setPhysicalNamingContext(parentInstance, parentInstance);
                }
            }
        }
        const instances = schematicInstances.get(parentInstance);
        if (!instances) {
            return;
        }
        const siblingPositionByFile = new Map();
        for (const child of instances) {
            const sheetName = child.sheetSymbol?.getSheetNameText();
            if (sheetName !== undefined) {
                const sourceFileCounts = fileDesignatorCounts.get(child.hierarchyEntry.sourceFile);
                if (!sourceFileCounts) {
                    throw new Error(`Invariant failed: Expected designator counts for ${child.hierarchyEntry.sourceFile}`);
                }
                const siblingPosition = (siblingPositionByFile.get(child.hierarchyEntry.sourceFile) ?? 0) + 1;
                siblingPositionByFile.set(child.hierarchyEntry.sourceFile, siblingPosition);
                child.numInstances = sourceFileCounts.get(child.channelPrefix);
                child.designWideInstances = this.resolveDesignWideInstances(child, designWideTotals);
                if (!child.isMultiChannel && (child.numInstances > 1 || child.totalSheetInstances > 1)) {
                    const ordinal = child.physicalChannelIndex ?? siblingPosition;
                    child.channelIndex = ordinal;
                    child.roomChannelIndex = ordinal;
                    child.physicalChannelIndex = ordinal;
                    child.roomAlphaIndex = ordinal - 1;
                }
                if (this.shouldInheritPhysicalNamingContext(parentInstance, child, instances)) {
                    this.setPhysicalNamingContext(child, parentInstance);
                    child.physicalChannelIndex = this.getPhysicalSiblingChannelIndex(parentInstance);
                    child.inheritsPhysicalNamingContext = true;
                }
                else {
                    this.setPhysicalNamingContext(child, child);
                    child.inheritsPhysicalNamingContext = false;
                }
            }
        }
        const sortedChildren = instances
            .map((instance) => [
            instance,
            instance.pathUniqueId
                .split('\\')
                .map((id) => id.padStart(13, '0'))
                .join('\\'),
        ])
            .sort(([instanceA, a], [instanceB, b]) => {
            let instanceAPri = 0;
            let instanceBPri = 0;
            if (instanceA.numInstances > 1 &&
                !instanceA.isMultiChannel &&
                this.annotationUtil.getChannelNameByUniqueId(instanceA.pathUniqueId)) {
                instanceAPri = 1;
            }
            if (instanceB.numInstances > 1 &&
                !instanceB.isMultiChannel &&
                this.annotationUtil.getChannelNameByUniqueId(instanceB.pathUniqueId)) {
                instanceBPri = 1;
            }
            if (instanceAPri !== instanceBPri) {
                return instanceBPri - instanceAPri;
            }
            return (0, AltiumChannelNameOrder_1.compareAltiumChannelNames)(a, b);
        });
        for (const [childInstance] of sortedChildren) {
            this.setChannelIndexes(childInstance, schematicInstances, fileDesignatorCounts, designWideTotals);
        }
    }
    computeDesignWideInstanceTotals(fileDesignatorCounts) {
        const designWideTotals = new Map();
        for (const [sourceFile, designatorCounts] of fileDesignatorCounts) {
            let total = 0;
            for (const count of designatorCounts.values()) {
                total += count;
            }
            designWideTotals.set(sourceFile, total);
        }
        return designWideTotals;
    }
    resolveDesignWideInstances(instance, designWideTotals) {
        const hasMultiInstanceAncestor = instance.path.some((ancestor) => ancestor.isMultiChannel || ancestor.numInstances > 1 || ancestor.totalSheetInstances > 1);
        if (hasMultiInstanceAncestor) {
            return undefined;
        }
        return designWideTotals.get(instance.hierarchyEntry.sourceFile);
    }
    shouldInheritPhysicalNamingContext(parentInstance, childInstance, siblingInstances) {
        if (childInstance.totalSheetInstances !== 1 || childInstance.numInstances <= 1) {
            return false;
        }
        if (parentInstance.isMultiChannel) {
            return false;
        }
        const directSiblingsForSameSheet = siblingInstances.filter((instance) => instance.hierarchyEntry.sourceFile === childInstance.hierarchyEntry.sourceFile &&
            instance.channelPrefix === childInstance.channelPrefix);
        if (directSiblingsForSameSheet.length !== 1) {
            return false;
        }
        return parentInstance.numInstances === 1 && parentInstance.totalSheetInstances > 1;
    }
    getPhysicalSiblingChannelIndex(instance) {
        const parent = instance.path.at(-1);
        if (!parent) {
            return instance.physicalChannelIndex ?? instance.channelIndex;
        }
        const siblingInstances = parent.children.filter((sibling) => sibling.hierarchyEntry.sourceFile === instance.hierarchyEntry.sourceFile);
        const siblingIndex = siblingInstances.indexOf(instance);
        if (siblingIndex === -1) {
            return instance.physicalChannelIndex ?? instance.channelIndex;
        }
        return siblingIndex + 1;
    }
    setPhysicalNamingContext(target, source) {
        target.physicalRoomName = source.physicalRoomName ?? source.roomName;
        target.physicalChannelPrefix = source.physicalChannelPrefix ?? source.channelPrefix;
        target.physicalChannelIndex = source.physicalChannelIndex ?? source.channelIndex;
    }
    resolvePhysicalNetNames() {
        const hierarchy = this.buildCombobulationHierarchy();
        const physicalNetGroups = this.namingStrategy.resolvePhysicalNetNames(hierarchy);
        const netGroupsMap = new Map();
        for (let i = 0; i < physicalNetGroups.length; i++) {
            netGroupsMap.set(`group_${i}`, physicalNetGroups[i]);
        }
        this.updateNetRefsToPhysicalNames(netGroupsMap);
        this.physicalNetGroups = netGroupsMap;
        return netGroupsMap;
    }
    buildCombobulationHierarchy() {
        const rootInstances = Array.from(this.instanceMap.values()).filter((inst) => inst.path.length === 0);
        if (rootInstances.length === 0) {
            throw new Error('No root instances found in hierarchy');
        }
        const rootNodes = rootInstances.map((rootInstance) => {
            const rootInfo = this.instances.get(rootInstance.instanceId);
            if (!rootInfo) {
                throw new Error(`No instance info found for root instance ${rootInstance.instanceId}`);
            }
            return this.buildCombobulationHierarchyNode(rootInstance, rootInfo);
        });
        if (rootNodes.length === 1) {
            return rootNodes[0];
        }
        return this.namingStrategy.createVirtualRootHierarchy(rootNodes);
    }
    buildCombobulationHierarchyNode(instance, instanceInfo) {
        const children = [];
        for (const [childPathId, childInfo] of this.instances.entries()) {
            const childInstance = this.instanceMap.get(childPathId);
            if (!childInstance) {
                continue;
            }
            if (childInstance.path.length === instance.path.length + 1 &&
                childInstance.path[childInstance.path.length - 1] === instance) {
                children.push(this.buildCombobulationHierarchyNode(childInstance, childInfo));
            }
        }
        return {
            pathUniqueId: instance.pathUniqueId,
            hierarchyDepth: instance.path.length,
            instance,
            combobulator: instanceInfo.combobulator,
            children,
        };
    }
    getFinalModel() {
        if (!this.physicalNetGroups) {
            throw new Error('Physical net names not resolved');
        }
        const layerSets = new Set();
        const finalizedTransformers = new Set();
        for (const [_instance, transformer] of this.schematicTransformers) {
            if (!finalizedTransformers.has(transformer)) {
                transformer.finalize(Array.from(this.physicalNetGroups.values()));
                finalizedTransformers.add(transformer);
                layerSets.add(transformer.model.layerSet);
            }
        }
        const layerSetsDefs = (0, layers_1.getAltiumDefaultLayerSets)();
        for (const layerSet of layerSets) {
            if (!this.projectModel.layerSets[layerSet]) {
                if (!layerSetsDefs[layerSet]) {
                    throw new Error(`Layer set ${layerSet} not found in default layer sets`);
                }
                this.projectModel.layerSets[layerSet] = layerSetsDefs[layerSet].layers;
            }
        }
        const allExtractions = new Map();
        for (const transformer of finalizedTransformers) {
            for (const extraction of transformer.extractedImages) {
                if (!allExtractions.has(extraction.ref)) {
                    allExtractions.set(extraction.ref, {
                        type: sigma_engine_shared_1.ProjectModelAssetType.Image,
                        name: extraction.name,
                        mimeType: extraction.mimeType,
                    });
                }
            }
        }
        if (allExtractions.size > 0) {
            this.projectModel.assets = Array.from(allExtractions.entries()).map(([ref, { type, name, mimeType }]) => ({
                type,
                ref,
                name,
                mimeType,
            }));
        }
        const moduleNames = new Set(this.projectModel.modules.map((m) => m.name));
        if (this.projectModel.modules.length !== moduleNames.size) {
            throw new Error('Duplicate module names found in project model');
        }
        return this.projectModel;
    }
    getExtractedImages() {
        const seen = new Map();
        for (const transformer of this.schematicTransformers.values()) {
            for (const extraction of transformer.extractedImages) {
                if (!seen.has(extraction.ref)) {
                    seen.set(extraction.ref, extraction);
                }
            }
        }
        return Array.from(seen.values());
    }
    buildDesignHierarchy() {
        const roots = [];
        for (const rootHierarchy of this.rootHierarchies) {
            const rootInstance = this.hierarchyInstances.find((inst) => inst.hierarchyEntry === rootHierarchy && inst.path.length === 0);
            if (!rootInstance) {
                continue;
            }
            roots.push(this.buildHierarchyNode(rootInstance));
        }
        return roots;
    }
    buildHierarchyNode(instance) {
        const transformer = this.schematicTransformers.get(instance.hierarchyEntry.sourceFile);
        const designId = transformer?.model.designId ?? instance.hierarchyEntry.sourceFile;
        const isMultiInstance = instance.totalSheetInstances > 1;
        const instanceId = isMultiInstance ? instance.pathUniqueId : undefined;
        let label = `[${instance.hierarchyEntry.schematicIndex + 1}] ${sigma_engine_shared_1.Files.basenamePath(instance.hierarchyEntry.sourceFile)}`;
        if (isMultiInstance && instance.isMultiChannel && instance.roomName) {
            label = instance.roomName;
        }
        const children = this.buildHierarchyChildren(instance.children.sort((a, b) => {
            const sheetIndexDiff = a.hierarchyEntry.schematicIndex - b.hierarchyEntry.schematicIndex;
            if (sheetIndexDiff !== 0) {
                return sheetIndexDiff;
            }
            return a.channelIndex - b.channelIndex;
        }));
        return {
            designId,
            label,
            ...(instanceId !== undefined && { instanceId }),
            ...(children.length > 0 && { children }),
        };
    }
    buildHierarchyChildren(children) {
        const result = [];
        const groupsBySourceFile = new Map();
        const insertionOrder = [];
        for (const child of children) {
            const sourceFile = child.hierarchyEntry.sourceFile;
            let group = groupsBySourceFile.get(sourceFile);
            if (!group) {
                group = [];
                groupsBySourceFile.set(sourceFile, group);
                insertionOrder.push(sourceFile);
            }
            group.push(child);
        }
        for (const sourceFile of insertionOrder) {
            const group = groupsBySourceFile.get(sourceFile).sort((a, b) => {
                const sheetIndexDiff = a.hierarchyEntry.schematicIndex - b.hierarchyEntry.schematicIndex;
                if (sheetIndexDiff !== 0) {
                    return sheetIndexDiff;
                }
                return a.channelIndex - b.channelIndex;
            });
            if (group.length <= 1 || group[0].totalSheetInstances <= 1) {
                for (const child of group) {
                    result.push(this.buildHierarchyNode(child));
                }
            }
            else {
                const transformer = this.schematicTransformers.get(sourceFile);
                const overviewDesignId = transformer?.model.designId ?? sourceFile;
                const instanceChildren = group.map((child) => this.buildHierarchyNode(child));
                result.push({
                    designId: overviewDesignId,
                    label: `[${group[0].hierarchyEntry.schematicIndex + 1}] ${sigma_engine_shared_1.Files.basenamePath(sourceFile)}`,
                    children: instanceChildren,
                });
            }
        }
        return result;
    }
    createInstanceCombobulator(instanceId) {
        if (this.instances.has(instanceId)) {
            throw new Error(`Instance ${instanceId} already has a combobulator`);
        }
        const instance = this.instanceMap.get(instanceId);
        if (!instance) {
            throw new Error(`Instance ${instanceId} not found in hierarchy`);
        }
        const netRefCtx = this.flatNamingContext ?? new sigma_engine_shared_1.NetRefCtx();
        const combobulator = new NetCombobulator_1.NetCombobulator(netRefCtx, this.namingStrategy, instance, {
            autoAssignNets: false,
            pointRoundingPrecision: AltiumCombobulatorConfig_1.ALTIUM_POINT_ROUNDING_PRECISION,
            connectionTolerance: AltiumCombobulatorConfig_1.ALTIUM_CONNECTION_TOLERANCE,
        });
        this.instances.set(instanceId, {
            instanceId,
            instance,
            combobulator,
            netRefCtx,
        });
        return combobulator;
    }
    getInstanceCombobulator(instanceId) {
        const cb = this.instances.get(instanceId)?.combobulator;
        if (!cb) {
            throw new Error(`Instance ${instanceId} not found in hierarchy`);
        }
        return cb;
    }
    getInstanceInfo(pathUniqueId) {
        const info = this.instances.get(pathUniqueId);
        if (!info) {
            throw new Error(`Instance ${pathUniqueId} not found in hierarchy`);
        }
        return info;
    }
    updateNetRefsToPhysicalNames(netGroups) {
        for (const [groupId, group] of netGroups) {
            if (!group.physicalName) {
                throw new Error(`No physical name for net group ${groupId}`);
            }
            for (const [instance, nets] of group.getNets()) {
                const instanceInfo = this.instances.get(instance.pathUniqueId);
                if (!instanceInfo) {
                    continue;
                }
                for (const net of nets) {
                    const netRef = net.name;
                    if (netRef.valueOf() !== group.physicalName) {
                        netRef.setName(group.physicalName);
                    }
                    for (const ref of net.netRefs) {
                        ref.setName(group.physicalName);
                    }
                }
            }
        }
        for (const instanceInfo of this.instances.values()) {
            instanceInfo.netRefCtx.clearOldRefs();
        }
    }
}
exports.AltiumHierarchicalSchematicProcessor = AltiumHierarchicalSchematicProcessor;
//# sourceMappingURL=AltiumHierarchicalSchematicProcessor.js.map